/** AI B2B Leads: API shapes and the filter-form helpers shared by the page and its dialogs. */

export type FilterKind = "text_list" | "multi_select" | "single_select";

export interface FilterFieldSpec {
  key: string;
  label: string;
  kind: FilterKind;
  hint: string;
  options?: string[];
  maxItems?: number;
  maxLength?: number;
  required?: boolean;
  /** A long list of options: show a dropdown with a search box, not one button per option. */
  searchable?: boolean;
}

/** One enrichment field: something leads.cm knows about a person or their company that can be shown in the table and exported. */
export interface ColumnSpec {
  key: string;
  label: string;
  group: string;
  kind: "text" | "link" | "long";
  hint?: string;
}

export interface Vocabulary {
  status: "provisional" | "verified" | string;
  fields: FilterFieldSpec[];
  /** Every enrichment field, in the order they are shown and exported. */
  columns?: ColumnSpec[];
  columnGroups?: Array<{ key: string; label: string }>;
  /** What the table shows until someone chooses. */
  defaultColumns?: string[];
  limits: { csvMaxMb: number; csvMaxRows: number; promptMaxChars: number; maxResults: number; maxSelection: number };
}

/** What the user is typing: comma-separated text, chosen options, or one chosen value, per field key. */
export type FormValues = Record<string, string | string[]>;
export type LeadFilters = Record<string, string | string[]>;

export function emptyValues(fields: FilterFieldSpec[]): FormValues {
  const v: FormValues = {};
  for (const f of fields) v[f.key] = f.kind === "multi_select" ? [] : "";
  return v;
}

export function valuesToFilters(values: FormValues, fields: FilterFieldSpec[]): LeadFilters {
  const out: LeadFilters = {};
  for (const f of fields) {
    const v = values[f.key];
    if (f.kind === "single_select" && typeof v === "string") {
      if (v.trim()) out[f.key] = v.trim();
    } else if (f.kind === "text_list" && typeof v === "string") {
      const seen = new Set<string>();
      const list = v.split(/[,\n]/).map((x) => x.trim()).filter((x) => x && !seen.has(x.toLowerCase()) && !!seen.add(x.toLowerCase()));
      if (list.length) out[f.key] = list.slice(0, f.maxItems ?? 10);
    } else if (f.kind === "multi_select" && Array.isArray(v)) {
      if (v.length) out[f.key] = v;
    }
  }
  return out;
}

export function filtersToValues(filters: LeadFilters, fields: FilterFieldSpec[]): FormValues {
  const v = emptyValues(fields);
  for (const f of fields) {
    const x = filters[f.key];
    if (f.kind === "single_select" && typeof x === "string") v[f.key] = x;
    else if (f.kind === "text_list" && Array.isArray(x)) v[f.key] = x.join(", ");
    else if (f.kind === "multi_select" && Array.isArray(x)) v[f.key] = x;
  }
  return v;
}

/**
 * Which page numbers to show: always the first and last, the current one and its neighbours, and "gap" where pages are skipped.
 * A gap of a single page shows that page instead, so "1 … 3" never hides just one number.
 */
export function pageWindow(current: number, total: number): Array<number | "gap"> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const keep = new Set<number>([1, total, current - 1, current, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => keep.add(n));
  if (current >= total - 2) [total - 1, total - 2, total - 3].forEach((n) => keep.add(n));
  const nums = [...keep].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out: Array<number | "gap"> = [];
  let prev = 0;
  for (const n of nums) {
    if (prev && n - prev === 2) out.push(prev + 1);
    else if (prev && n - prev > 2) out.push("gap");
    out.push(n);
    prev = n;
  }
  return out;
}

/** "linkedin.com/in/ada-lovelace-1a2b": the profile address without the scheme, for showing in a table cell. */
export function linkedinLabel(url: string): string {
  const bare = url.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/+$/, "");
  try { return decodeURIComponent(bare); } catch { return bare; }
}

/** Only a web address may become a link: never `javascript:` or anything else a stored value could hold. */
export function safeHref(url: string | null | undefined): string | null {
  return url && /^https?:\/\//i.test(url) ? url : null;
}

/** One line per filter, for "copy as text". `label` lets the caller translate field names and option values. */
export function describeFilters(filters: LeadFilters, fields: FilterFieldSpec[], label: (f: FilterFieldSpec) => string, option: (f: FilterFieldSpec, o: string) => string): string {
  const lines: string[] = [];
  for (const f of fields) {
    const x = filters[f.key];
    if (typeof x === "string") lines.push(`${label(f)}: ${x}`);
    else if (Array.isArray(x)) lines.push(`${label(f)}: ${x.map((o) => (f.kind === "multi_select" ? option(f, o) : o)).join(", ")}`);
  }
  return lines.join("\n");
}

export interface ProspectLead {
  id: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  seniority: string | null;
  company: string | null;
  companyWebsite: string | null;
  industry: string | null;
  companySize: string | null;
  location: string | null;
  email: string | null;
  phone: string | null;
  /** "company" = the employer's switchboard rather than the person's own number. */
  phoneKind: "direct" | "company" | null;
  linkedinUrl: string | null;
  source: string;
  sourceFile: string | null;
  status: "new" | "added" | "result";
  addedCampaignId: string | null;
  hasPhone: boolean;
  phoneHasCountryCode: boolean;
  /** One plain string per enrichment field (empty when the lead has none), keyed by ColumnSpec.key. */
  details?: Record<string, string>;
}

export interface LeadCounts { total: number; withPhone: number; withoutPhone: number; added: number }

export interface LeadsResponse {
  data: ProspectLead[];
  pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
  counts: LeadCounts;
}

export interface ImportResult {
  jobId: string;
  rowsRead: number;
  rowsInvalid: number;
  duplicatesInFile: number;
  duplicatesAlreadySaved: number;
  enrichedExisting: number;
  saved: number;
  savedWithPhone: number;
  savedWithoutPhone: number;
  savedWithCompanyLine: number;
  recognisedColumns: string[];
  unmappedColumns: string[];
  hasPhoneColumn: boolean;
  warnings: string[];
}

export interface ParsedPrompt {
  filters: LeadFilters;
  /** How the request was read, built by the server from the filters that survived. */
  explanation: string;
  maxResults: number;
  /** Things that were changed or dropped. */
  warnings: string[];
  /** Things the request asked for that no filter can do (or only roughly). */
  notApplied: string[];
}

export interface AddToCampaignResult {
  requested: number;
  notFound: number;
  added: number;
  skippedNoPhone: number;
  skippedDuplicate: number;
  withoutCountryCode: number;
  countryCodeAdded?: number;
  companyLines: number;
  campaignId: string;
  campaignName: string;
}

/**
 * The comparison the server uses to decide two numbers are the same (last 10 digits when there are that many), so the
 * add-to-campaign dialog can tell how many of the selected leads share one number before anything is sent.
 */
export const phoneKey = (phone: string | null | undefined): string | null => {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length < 7) return null;
  return digits.length >= 10 ? digits.slice(-10) : digits;
};

export const leadName = (l:Pick<ProspectLead, "firstName" | "lastName" | "email">): string =>
  [l.firstName, l.lastName].filter(Boolean).join(" ") || l.email || "-";

// ---- background search on leads.cm ----

export type SearchJobStatus = "queued" | "running" | "completed" | "failed" | "cancelled";

export interface SearchStatus {
  available: boolean;
  /** not_configured | disabled | paused | worker_offline, or null when a search can start. */
  reason: string | null;
  message: string | null;
  enabled: boolean;
  configured: boolean;
  workerOnline: boolean;
  cooldownUntil: string | null;
  mode: string;
  limits: { maxResults: number; defaultMaxResults: number; defaultDatasets: number; maxDatasetsPerJob: number; perUserPerDay: number };
}

export interface SearchJobView {
  id: string;
  status: SearchJobStatus;
  phase: string | null;
  phasePercent: number;
  country: string | null;
  filters: LeadFilters;
  summary: string;
  maxResults: number | null;
  datasetFrom: number | null;
  maxDatasets: number | null;
  datasetsTotal: number | null;
  datasetsDone: number;
  currentDataset: number | null;
  /** How many leads this search has found so far. */
  results: number;
  matchesSeen: number;
  optedOut: number;
  /** target_reached | datasets_exhausted | dataset_cap | cancelled */
  stopReason: string | null;
  nextDataset: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface SearchResponse {
  job: SearchJobView;
  results: { data: ProspectLead[]; pagination: { page: number; pageSize: number; totalItems: number; totalPages: number } };
}

export const isActiveJob = (j: Pick<SearchJobView, "status"> | undefined | null): boolean => j?.status === "queued" || j?.status === "running";
